import { useCallback, useRef, useState } from 'react';
import { useStore } from '../store/useStore';
import { useT } from '../i18n/useT';
import { useDocControllerContext } from '../hooks/DocControllerContext';
import { prepareImport } from '../lib/import';
import { Icon } from './Icon';
import { Modal } from './Modal';

/**
 * Import files by dropping them, or by picking them: Markdown and text as they
 * are, Word and HTML converted on the device (see lib/convertImport.ts).
 *
 * The command used to open the OS file picker straight away. That works, but it
 * hides the one gesture people reach for first — dragging a file onto the
 * window — behind knowing it exists. This dialog puts both in the same place:
 * the drop target *is* the picker button, so dropping and browsing are one
 * affordance rather than two competing ones.
 *
 * It is a `<button>` rather than a styled `<div>` so it is reachable by Tab and
 * activates on Enter and Space. A drop zone that only works with a mouse is a
 * drop zone that excludes people.
 */

const ACCEPT = [
  '.md,.markdown,.mdown,.mkd,.mdwn,.txt,.zip,.docx,.html,.htm,.enex',
  'text/markdown,text/plain,text/html,application/zip',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
].join(',');

export function ImportDialog() {
  const t = useT();
  const open = useStore((s) => s.importOpen);
  const setOpen = useStore((s) => s.setImportOpen);
  const controller = useDocControllerContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Names we refused, so the dialog can say so instead of ignoring them. */
  const [skipped, setSkipped] = useState<string[]>([]);
  /** Archives that could not be opened, already phrased for display. */
  const [unreadable, setUnreadable] = useState<string[]>([]);
  /** Word or HTML files whose conversion failed. */
  const [failed, setFailed] = useState<string[]>([]);

  // dragenter/dragleave also fire when the pointer crosses a *child* element,
  // so a boolean flag flickers as you move over the label inside the zone.
  // Counting enter/leave pairs tracks the zone as a whole.
  const depth = useRef(0);

  const close = useCallback(() => {
    setOpen(false);
    setOver(false);
    setBusy(false);
    setSkipped([]);
    setUnreadable([]);
    setFailed([]);
    depth.current = 0;
  }, [setOpen]);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      setBusy(true);
      const { accepted, rejected, unreadable } = await prepareImport(files);
      setBusy(false);
      setSkipped(rejected);
      setUnreadable(unreadable.map((u) => t(`import.zip.${u.code}`, { name: u.name })));

      if (accepted.length === 0) return;

      setBusy(true);
      const notConverted = await controller.importFiles(accepted).finally(() => setBusy(false));
      setFailed(notConverted);
      // Everything landed, so the dialog has nothing left to say. When files
      // were skipped it stays open — closing would swallow the only explanation
      // the user is going to get.
      if (rejected.length === 0 && unreadable.length === 0 && notConverted.length === 0) close();
    },
    [controller, close, t],
  );

  if (!open) return null;

  const hasFiles = (e: React.DragEvent): boolean =>
    Array.from(e.dataTransfer.items).some((i) => i.kind === 'file');

  return (
    <Modal onClose={close} title={t('import.title')}>
      <p className="mb-modal-note">{t('import.hint')}</p>

      <button
        type="button"
        className={`mb-dropbox${over ? ' mb-dropbox--over' : ''}`}
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        aria-describedby="mb-import-formats"
        onDragEnter={(e) => {
          if (!hasFiles(e)) return;
          // Every drag event here is also stopped from bubbling. The whole app
          // is a drop target, and it sits above this dialog in the tree: without
          // this, dropping a file on the zone imports it twice — once here and
          // once on the way up — and the app's full-window overlay lights up
          // behind the modal while you drag.
          e.stopPropagation();
          depth.current += 1;
          setOver(true);
        }}
        onDragOver={(e) => {
          if (!hasFiles(e)) return;
          // Without preventDefault the browser refuses the drop and opens the
          // file in a new tab instead.
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragLeave={(e) => {
          e.stopPropagation();
          depth.current = Math.max(0, depth.current - 1);
          if (depth.current === 0) setOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          depth.current = 0;
          setOver(false);
          void handleFiles(e.dataTransfer.files);
        }}
      >
        <Icon name="upload" size={28} className="mb-dropbox__icon" />
        <span className="mb-dropbox__label">{busy ? t('import.importing') : t('import.drop')}</span>
        <span className="mb-dropbox__hint">{t('import.browse')}</span>
      </button>

      <p className="mb-modal-note mb-dropbox__formats" id="mb-import-formats">
        {t('import.formats')}
      </p>

      {/* Live, so a screen reader hears what was skipped without hunting for it. */}
      <div aria-live="polite">
        {skipped.length > 0 && (
          <p className="mb-modal-note mb-import-error">
            {t('import.skipped', { files: skipped.join(', ') })}
          </p>
        )}
        {unreadable.map((message) => (
          <p key={message} className="mb-modal-note mb-import-error">
            {message}
          </p>
        ))}
        {failed.length > 0 && (
          <p className="mb-modal-note mb-import-error">
            {t('import.failed', { files: failed.join(', ') })}
          </p>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        hidden
        onChange={(e) => {
          // Snapshot before clearing: handleFiles reads the list asynchronously,
          // and resetting value empties the live FileList.
          const files = e.target.files ? Array.from(e.target.files) : [];
          e.target.value = '';
          if (files.length) void handleFiles(files);
        }}
      />

      <div className="mb-modal-actions">
        <button type="button" className="mb-btn" onClick={close}>
          {skipped.length > 0 || unreadable.length > 0 || failed.length > 0
            ? t('common.close')
            : t('common.cancel')}
        </button>
      </div>
    </Modal>
  );
}
